import React from 'react';
import {
  type AttributeGroup,
  type ProductGroupTreeForm,
  type ProductLeaf,
  type SectionPath,
  isAttributeGroup,
  isProductLeaf,
} from './tree';
import { AddPill } from '../components/tree/AddPill';
import { LeafProductCell } from '../components/tree/LeafProductCell';
import { SectionValueCell } from '../components/tree/SectionValueCell';

export interface EditorRow {
  key: string;
  rowClasses: string[];
  cells: (React.ReactNode | null)[];
}

interface PendingPill {
  column: number;
  label: string;
  onClick: () => void;
}

export interface EditorRowHandlers {
  updateSectionValue: (path: SectionPath, value: string) => void;
  moveSectionWithinParent: (path: SectionPath, direction: 1 | -1) => void;
  removeSection: (path: SectionPath) => void;
  removeLeaf: (parentPath: SectionPath, childIndex: number) => void;
  addSubSectionInSection: (path: SectionPath) => void;
  addNewTopLevelSection: () => void;
  openPickerForSection: (path: SectionPath) => void;
}

interface BuildEditorRowsInput {
  form: ProductGroupTreeForm;
  isDisabled: boolean;
  totalColumns: number;
  handlers: EditorRowHandlers;
}

export function buildProductGroupEditorRows(input: BuildEditorRowsInput): EditorRow[] {
  const { form, isDisabled, totalColumns, handlers } = input;
  const rows: EditorRow[] = [];

  const emptyCells = (): (React.ReactNode | null)[] => Array.from({ length: totalColumns }, () => null);

  const renderSectionValueCell = (
    section: AttributeGroup,
    path: SectionPath,
    depth: number,
    totalSiblings: number,
  ): React.ReactNode => (
    <SectionValueCell
      section={section}
      path={path}
      depth={depth}
      totalSiblings={totalSiblings}
      attributeName={form.attributes[depth] || ''}
      disabled={isDisabled}
      onValueChange={handlers.updateSectionValue}
      onMove={handlers.moveSectionWithinParent}
      onRemove={handlers.removeSection}
    />
  );

  const buildAddPillRow = (keySuffix: string, pills: PendingPill[], isFinalRow: boolean): EditorRow => {
    const cells = emptyCells();
    pills.forEach(({ column, label, onClick }) => {
      cells[column] = <AddPill label={label} onClick={onClick} disabled={isDisabled} />;
    });
    const classes = ['pgte-add-row'];
    if (isFinalRow) classes.push('pgte-add-row-final');
    return { key: `add-${keySuffix}`, rowClasses: classes, cells };
  };

  const renderLeafProductCell = (leaf: ProductLeaf, parentPath: SectionPath, childIndex: number) => (
    <LeafProductCell
      leaf={leaf}
      parentPath={parentPath}
      childIndex={childIndex}
      disabled={isDisabled}
      onRemove={handlers.removeLeaf}
    />
  );

  /**
   * Recursively walk a section, emitting content rows directly into `rows`.
   *
   * Layout: ancestor + own value-editor cells are injected into the FIRST emitted row of this
   * section; later rows leave them blank, producing the visual grouping:
   *
   *     | color (input) | size (input) | sku 1   | x |
   *     |               |              | sku 2   | x |   ← continuation
   *     |               |              | + Add product |
   *     |               | + Add size                   |
   *
   * Returns this section's "trailing" Add-pills (the ones that belong AFTER its content). The
   * caller either emits them right away (if this isn't the last sibling) or bubbles them up to
   * be merged with the parent's pills — that's what flattens the ladder of + buttons into a
   * single row at the bottom of each last-child chain.
   */
  const walkSection = (
    section: AttributeGroup,
    path: SectionPath,
    depth: number,
    pendingAncestorCells: Map<number, React.ReactNode>,
    incomingBreakDepth: number,
    totalSiblings: number,
  ): PendingPill[] => {
    const ownEditor = renderSectionValueCell(section, path, depth, totalSiblings);

    const leafChildren: { leaf: ProductLeaf; index: number }[] = [];
    const subSectionChildren: { group: AttributeGroup; index: number }[] = [];
    section.children.forEach((child, index) => {
      if (isProductLeaf(child)) leafChildren.push({ leaf: child, index });
      else if (isAttributeGroup(child)) subSectionChildren.push({ group: child, index });
    });

    let isFirstRowOfThisSection = true;
    let isFirstEmittedRow = true;

    const buildRowCellsWithAncestors = (productCell: React.ReactNode | null) => {
      const cells = emptyCells();
      if (isFirstRowOfThisSection) {
        pendingAncestorCells.forEach((cell, columnIndex) => {
          cells[columnIndex] = cell;
        });
        cells[depth] = ownEditor;
      }
      cells[totalColumns - 1] = productCell;
      return cells;
    };

    leafChildren.forEach(({ leaf, index }) => {
      const cells = buildRowCellsWithAncestors(renderLeafProductCell(leaf, path, index));
      const classes: string[] = [];
      if (isFirstEmittedRow) classes.push(`pgte-break-d${incomingBreakDepth}`);
      rows.push({ key: `leaf-${path.join('-')}-${index}`, rowClasses: classes, cells });
      isFirstRowOfThisSection = false;
      isFirstEmittedRow = false;
    });

    let pendingFromLastSubSection: PendingPill[] = [];
    subSectionChildren.forEach(({ group, index }, subIndex) => {
      const isLastSubSection = subIndex === subSectionChildren.length - 1;
      const ancestorsForChild = isFirstRowOfThisSection
        ? new Map<number, React.ReactNode>([
            ...Array.from(pendingAncestorCells.entries()),
            [depth, ownEditor],
          ])
        : new Map<number, React.ReactNode>();
      const childIncomingBreakDepth =
        subIndex === 0 && isFirstRowOfThisSection ? incomingBreakDepth : depth + 1;
      const childTrailing = walkSection(
        group,
        [...path, index],
        depth + 1,
        ancestorsForChild,
        childIncomingBreakDepth,
        section.children.length,
      );
      if (isLastSubSection) {
        pendingFromLastSubSection = childTrailing;
      } else if (childTrailing.length > 0) {
        rows.push(buildAddPillRow(`childtrail-${path.join('-')}-${index}`, childTrailing, false));
      }
      isFirstRowOfThisSection = false;
      isFirstEmittedRow = false;
    });

    if (leafChildren.length === 0 && subSectionChildren.length === 0) {
      const inlineAddPill = (
        <AddPill
          label="Add product"
          onClick={() => handlers.openPickerForSection(path)}
          disabled={isDisabled}
        />
      );
      const cells = buildRowCellsWithAncestors(inlineAddPill);
      const classes: string[] = [`pgte-break-d${incomingBreakDepth}`];
      rows.push({ key: `empty-${path.join('-')}`, rowClasses: classes, cells });
    }

    const ownTrailing: PendingPill[] = [];
    if (subSectionChildren.length > 0) {
      const nextAttributeName = form.attributes[depth + 1] || 'sub-attribute';
      ownTrailing.push({
        column: depth + 1,
        label: `Add ${nextAttributeName}`,
        onClick: () => handlers.addSubSectionInSection(path),
      });
    }

    return [...pendingFromLastSubSection, ...ownTrailing];
  };

  form.groups.forEach((group, groupIndex) => {
    const isLast = groupIndex === form.groups.length - 1;
    const trailing = walkSection(
      group,
      [groupIndex],
      0,
      new Map(),
      0,
      form.groups.length,
    );
    if (isLast) {
      const topAttribute = form.attributes[0] || 'attribute';
      const finalPills: PendingPill[] = [
        ...trailing,
        {
          column: 0,
          label: `Add ${topAttribute}`,
          onClick: handlers.addNewTopLevelSection,
        },
      ];
      rows.push(buildAddPillRow('final-trailing', finalPills, true));
    } else if (trailing.length > 0) {
      rows.push(buildAddPillRow(`section-trailing-${groupIndex}`, trailing, false));
    }
  });

  return rows;
}
